<template>
  <TransitionRoot
    :show="isOpen"
    as="template">
    <Dialog
      as="div"
      class="modal"
      open
      @close="close">
      <TransitionChild
        as="template"
        :enter="backdropTransition.enter"
        :enter-from="backdropTransition.enterFrom"
        :enter-to="backdropTransition.enterTo"
        :leave="backdropTransition.leave"
        :leave-from="backdropTransition.leaveFrom"
        :leave-to="backdropTransition.leaveTo">
        <div class="modal__backdrop" />
      </TransitionChild>
      <div class="modal__panel-wrapper">
        <TransitionChild
          as="template"
          :enter="panelTransition.enter"
          :enter-from="panelTransition.enterFrom"
          :enter-to="panelTransition.enterTo"
          :leave="panelTransition.leave"
          :leave-from="panelTransition.leaveFrom"
          :leave-to="panelTransition.leaveTo">
          <DialogPanel :class="panelClasses">
            <slot />
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import {
  Dialog,
  DialogPanel,
  TransitionRoot,
  TransitionChild,
} from "@headlessui/vue";
import type { TransitionClasses } from "../../../types";
import { computed } from "#imports";

export type ModalProps = {
  /**
   * Backdrop transition classes
   */
  backdropTransition?: TransitionClasses;

  /**
   * Panel transition classes
   */
  panelTransition?: TransitionClasses;

  /**
   * Prevent modal closing (defaults to true)
   */
  preventClose?: boolean;

  /**
   * Modal size
   */
  size?: "sm" | "md" | "lg" | "xl" | "fullscreen";

  /**
   * Show rounded corners
   */
  rounded?: boolean;
};

const props = withDefaults( defineProps<ModalProps>(), {
  backdropTransition: () => ( {
    enter: "modal__backdrop--enter",
    leave: "modal__backdrop--leave",
    enterFrom: "modal__backdrop--enter-from",
    enterTo: "modal__backdrop--enter-to",
    leaveFrom: "modal__backdrop--leave-from",
    leaveTo: "modal__backdrop--leave-to",
  } ),
  panelTransition: () => ( {
    enter: "modal__panel--enter",
    leave: "modal__panel--leave",
    enterFrom: "modal__panel--enter-from",
    enterTo: "modal__panel--enter-to",
    leaveFrom: "modal__panel--leave-from",
    leaveTo: "modal__panel--leave-to",
  } ),
  preventClose: true,
  size: "md",
  rounded: true,
} );

const panelClasses = computed( () => {
  return [
    "modal__panel",
    `modal__panel--${props.size}`,
    { "modal__panel--rounded": props.rounded && props.size !== "fullscreen" },
  ];
} );

const isOpen = defineModel<boolean>( {
  default: false,
} );

function close (): void {
  if ( props.preventClose ) {
    return;
  }

  isOpen.value = false;
}
</script>

<style scoped lang="scss">
.modal {

  &__backdrop {
    position: fixed;
    inset: 0;
    background-color: var(--backdrop-color);

    /*Modal backdrop transitions*/
    &--enter {
      transition: opacity 300ms ease-out;
    }

    &--leave {
      transition: opacity 200ms ease-in;
    }

    &--enter-from, &--leave-to {
      opacity: 0;
    }

    &--enter-to, &--leave-from {
      opacity: 1;
    }
  }

  &__panel-wrapper {
    overflow-y: auto;
    position: fixed;
    inset: 0;
    display: flex;
    width: 100dvw;
    justify-content: center;
    align-items: center;

    @media(max-width: 992px) {
      padding: 1rem;
    }
  }

  &__panel {
    background-color: var(--modal-background-color, #FFF);
    max-width: 100%;
    max-height: calc(100vh - 1rem);

    & > :first-child {
      overflow-y: auto;
      max-height: calc(100vh - 10rem);
      padding: var(--s-3);
    }

    /* Dynamic modal sizing */
    &--sm {
      width: var(--modal-sm, 220px);
    }

    &--md {
      width: var(--modal-md, 550px);
    }

    &--lg {
      width: var(--modal-lg, 800px);
    }

    &--xl {
      width: var(--modal-xl, 1100px);
    }

    &--fullscreen {
      height: 100%;
      width: 100%;
    }

    &--rounded {
      border-radius: 1rem;
    }

    /*Modal panel transitions*/
    &--enter {
      transition: opacity 300ms ease-out,
      transform 300ms ease-out;
    }

    &--leave {
      transition: opacity 200ms ease-in,
      transform 200ms ease-out;
    }

    &--enter-from, &--leave-to {
      opacity: 0;
      transform: translateY(-1rem) scale(0.975);
    }

    &--enter-to, &--leave-from {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
}
</style>
